import { useMemo, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import {
  type OnChangeFn,
  type PaginationState,
  type SortingState,
} from '@tanstack/react-table';

import { useGetScenarios } from '@/api/generated';
import { useScenariosProgressPolling } from '@/routes/(app)/(scenarios)/hooks/use-scenarios-progress-polling';
import { type Scenarios } from '@/routes/(app)/(scenarios)/types';
import {
  buildScenariosListParams,
  SCENARIOS_DEFAULT_PAGE_SIZE,
  SCENARIOS_DEFAULT_SORTING,
} from '@/routes/(app)/(scenarios)/utils/scenarios-list.utils';

export const useScenariosList = () => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: SCENARIOS_DEFAULT_PAGE_SIZE,
  });
  const [sorting, setSorting] = useState<SortingState>(
    SCENARIOS_DEFAULT_SORTING
  );

  const params = useMemo(
    () => buildScenariosListParams(pagination, sorting),
    [pagination, sorting]
  );

  const { data, isPlaceholderData, isLoading, isError, refetch } =
    useGetScenarios(params, {
      query: {
        placeholderData: keepPreviousData,
      },
    });

  const scenarios = useMemo<Scenarios>(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;

  useScenariosProgressPolling(scenarios, params, isPlaceholderData);

  const onSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting(updater);
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  };

  return {
    scenarios,
    total,
    pagination,
    onPaginationChange: setPagination,
    sorting,
    onSortingChange,
    isLoading,
    hasError: isError,
    refetch,
  };
};
